<!-- Top Header (Angular Material M3 Style) -->
<header class="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800 shadow-xs transition-colors duration-200">
  <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
    <div class="flex items-center justify-between h-16 gap-2">
      <!-- Left: Logo / Club Title & League Tag -->
      <div class="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
        <button
          type="button"
          (click)="game.openClubShieldEditor()"
          id="btn-header-customize-club-shield"
          aria-label="Personalizar escudo"
          title="Personalizar el escudo, colores y siglas del club"
          class="w-9 h-10 sm:w-10 sm:h-11 flex items-center justify-center shrink-0 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 transition-colors"
        >
          <app-club-shield [club]="game.userClub()" size="md" />
        </button>
        <div class="min-w-0 flex-1">
          <div class="flex items-center space-x-2">
            <h1 class="text-sm sm:text-lg font-bold text-slate-900 dark:text-white truncate">
              {{ game.userClub().name }}
            </h1>

            <!-- Competition Context Switcher (Mobile: Just an icon with arrows in each direction) -->
            @if (game.currentLeague(); as league) {
              <button
                type="button"
                (click)="toggleContextSwitcher()"
                id="btn-switch-league-mobile"
                [title]="game.t('nav.switch_league') + ' (' + league.name + ')'"
                [attr.aria-label]="game.t('nav.switch_league')"
                class="sm:hidden flex items-center justify-center w-7 h-7 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors cursor-pointer shrink-0 active:scale-95"
              >
                <app-icon name="arrow-left-right" [size]="14" class="text-slate-600 dark:text-slate-300" />
              </button>
            }

            <!-- Competition Context Switcher (GLOBAL / PRIVATE) - Desktop Pill -->
            @if (game.currentLeague(); as league) {
              <div class="relative hidden sm:block">
                <button
                  type="button"
                  (click)="toggleContextSwitcher()"
                  id="btn-switch-league"
                  [title]="game.t('nav.switch_league')"
                  class="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition-colors cursor-pointer"
                >
                  @if (league.type === 'GLOBAL') {
                    <app-icon name="globe" [size]="12" class="text-blue-600 dark:text-blue-400 shrink-0" />
                  } @else {
                    <app-icon name="shield" [size]="12" class="text-emerald-600 dark:text-emerald-400 shrink-0" />
                  }
                  <span class="truncate max-w-[120px] sm:max-w-[160px]">{{ league.name }}</span>
                  <span class="text-[10px] font-mono px-1.5 py-0.2 rounded bg-white/60 dark:bg-slate-900/80 text-slate-500 dark:text-slate-400 font-bold uppercase">
                    {{ league.type }}
                  </span>
                  <app-icon name="chevron-down" [size]="12" class="text-slate-400 shrink-0" />
                </button>
              </div>
            }
          </div>

          <!-- Subtitle / Club & League Info -->
          <div class="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 truncate flex items-center space-x-1.5 mt-0.5">
            <span class="truncate">
              @if (game.currentLeague()?.type === 'GLOBAL') { {{ game.userClub().division }} }
              <span class="hidden sm:inline">
                @if (game.currentLeague()?.type === 'GLOBAL') { · } {{ game.currentLeague()?.name }} (J{{ game.currentMatchdayNumber() }})
              </span>
            </span>
          </div>

          <!-- Shared Context Switcher Dropdown (Responsive: Modal on mobile, Popover on Desktop) -->
          @if (isContextSwitcherOpen()) {
            <!-- Overlay click-to-close -->
            <div (click)="closeContextSwitcher()" class="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-2xs"></div>

            <div
              id="context-switcher-dropdown"
              class="fixed inset-x-4 top-16 sm:absolute sm:inset-auto sm:left-12 sm:top-12 w-auto sm:w-80 max-w-sm mx-auto sm:mx-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl py-2 z-50 text-xs transition-colors"
            >
              <div class="px-3.5 py-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <span>{{ game.t('nav.competition_context') }}</span>
                <button
                  type="button"
                  (click)="closeContextSwitcher()"
                  class="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 cursor-pointer sm:hidden"
                >
                  <app-icon name="x" [size]="14" />
                </button>
              </div>

              <div class="p-1.5 max-h-64 overflow-y-auto space-y-1">
                @if (isLoadingCompetitions()) {
                  <p class="px-3 py-2 text-slate-500" role="status">{{ game.t('common.loading') }}</p>
                } @else if (competitionLoadFailed()) {
                  <p class="px-3 py-2 text-slate-500" role="status">{{ game.t('nav.leagues_load_failed') }}</p>
                }
                @for (l of competitionChoices(); track l.userClubId) {
                  <button
                    type="button"
                    (click)="switchContext(l.userClubId)"
                    [disabled]="!l.canPlay"
                    [id]="'switch-context-' + l.id"
                    class="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    [ngClass]="l.userClubId === game.activeUserClubId() ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 font-bold border border-emerald-200 dark:border-emerald-800/60' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 border border-transparent'"
                  >
                    <div class="flex items-center space-x-2 truncate">
                      @if (l.type === 'GLOBAL') {
                        <app-icon name="globe" [size]="15" class="text-blue-600 dark:text-blue-400 shrink-0" />
                      } @else {
                        <app-icon name="shield" [size]="15" class="text-emerald-600 dark:text-emerald-400 shrink-0" />
                      }
                      <div class="truncate">
                        <p class="text-xs truncate leading-tight">{{ l.name }}</p>
                        <p class="text-[10px] text-slate-400 dark:text-slate-500 font-normal">Club: {{ l.userClubName }}</p>
                      </div>
                    </div>

                    <div class="flex items-center space-x-1.5 shrink-0 ml-2">
                      <span
                        class="text-[9px] px-1.5 py-0.5 rounded font-mono font-bold uppercase"
                        [ngClass]="l.type === 'GLOBAL' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300'"
                      >
                        {{ l.type }}
                      </span>
                      @if (l.userClubId === game.activeUserClubId()) {
                        <app-icon name="check" [size]="14" class="text-emerald-600 dark:text-emerald-400" />
                      }
                    </div>
                  </button>
                }
              </div>

              <div class="pt-1.5 border-t border-slate-100 dark:border-slate-800 px-1.5">
                <button
                  type="button"
                  (click)="game.returnToLeagueSelection(); closeContextSwitcher()"
                  id="btn-manage-all-leagues"
                  class="w-full flex items-center space-x-2 px-3 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-[11px] font-semibold transition-colors cursor-pointer"
                >
                  <app-icon name="arrow-left-right" [size]="13" />
                  <span>{{ game.t('nav.all_leagues') }}</span>
                </button>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Middle: Key Financial Indicators (Visible on Mobile & Desktop) -->
      <div class="flex items-center shrink-0">
        <a
          [routerLink]="['/club']"
          [queryParams]="{ tab: 'economy' }"
          id="btn-nav-budget"
          class="flex items-center space-x-1.5 sm:space-x-2.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl bg-emerald-50/80 hover:bg-emerald-100 dark:bg-slate-800/80 dark:hover:bg-slate-700/80 border border-emerald-200/80 dark:border-slate-700/80 shadow-2xs transition-all duration-150 cursor-pointer group active:scale-98"
          title="Ver tesorería y economía del club (/club?tab=economy)"
        >
          <app-icon name="coins" [size]="16" class="text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform shrink-0" />
          <div class="text-left">
            <div class="text-[9px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold hidden sm:flex items-center space-x-1">
              <span>{{ game.t('nav.budget') }}</span>
              <app-icon name="chevron-right" [size]="10" class="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400" />
            </div>
            <div class="text-xs sm:text-sm font-bold text-emerald-700 dark:text-emerald-400 font-mono whitespace-nowrap">
              {{ game.balanceData() ? ((game.balanceData()!.availableBalance / 1000000).toFixed(2) + ' M€') : '—' }}
            </div>
          </div>
        </a>
      </div>

      <!-- Right Action Controls: Settings & User Menu Trigger (Always Visible) -->
      <div class="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
        <!-- User & Settings Menu Trigger (Guaranteed Visible on All Screen Sizes) -->
        <button
          type="button"
          (click)="toggleUserMenu()"
          id="btn-user-settings"
          [title]="game.t('nav.settings')"
          aria-label="Ajustes y Menú de Mánager"
          class="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-2.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-all cursor-pointer shadow-xs shrink-0 active:scale-95"
        >
          <!-- Avatar Initial -->
          <div class="w-6 h-6 rounded-lg bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
            {{ game.currentUser()?.name?.charAt(0) || 'M' }}
          </div>
          <span class="hidden md:inline text-xs font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[110px]">
            {{ game.currentUser()?.name || 'Mánager' }}
          </span>
          <!-- Settings Icon Clearly Visible -->
          <app-icon name="settings" [size]="16" class="text-slate-600 dark:text-slate-300 shrink-0" />
        </button>

        <!-- Desktop Dropdown Menu (Anchored to Trigger) -->
        @if (isUserMenuOpen()) {
          <!-- Desktop Backdrop -->
          <div (click)="closeUserMenu()" class="hidden sm:block fixed inset-0 z-40"></div>

          <div
            id="user-menu-dropdown"
            class="hidden sm:block absolute right-4 top-16 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl py-2 z-50 text-xs transition-colors"
          >
            <!-- User Info Header -->
            @if (game.currentUser(); as user) {
              <div class="px-3.5 py-2.5 border-b border-slate-100 dark:border-slate-800">
                <div class="flex items-center space-x-2.5">
                  <div class="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-bold text-sm shadow-xs shrink-0">
                    {{ user.name.charAt(0) || 'M' }}
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="font-bold text-slate-900 dark:text-white truncate">{{ user.name }}</p>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">{{ user.email }}</p>
                  </div>
                </div>
                @if (game.userClub(); as club) {
                  <div class="mt-2 flex items-center space-x-1.5">
                    <span class="w-2 h-2 rounded-full" [style.backgroundColor]="club.primaryColor"></span>
                    <span class="text-[11px] font-semibold text-slate-700 dark:text-slate-300 truncate">{{ club.name }}</span>
                  </div>
                }
              </div>
            }

            <!-- Manager Wallet Card
            <div class="p-3 mx-2 my-1.5 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 dark:from-amber-950/50 dark:to-orange-950/30 border border-amber-200/90 dark:border-amber-800/60">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-1.5">
                  <app-icon name="coins" [size]="15" class="text-amber-600 dark:text-amber-400" />
                  <span class="font-bold text-[11px] text-amber-900 dark:text-amber-200">
                    {{ game.t('settings.wallet_title') }}
                  </span>
                </div>
                <span class="font-mono font-bold text-amber-700 dark:text-amber-300 text-xs px-2 py-0.5 rounded-md bg-white/90 dark:bg-slate-900 border border-amber-200/80 dark:border-amber-800/50 shadow-2xs">
                  {{ game.userClub().economy.subscriptionCredits }} CR
                </span>
              </div>
              <p class="text-[10px] text-amber-800/80 dark:text-amber-300/70 mt-1 leading-tight">
                Saldo personal para cuota mensual de licencia (100 CR/mes) y torneos.
              </p>
              <div class="mt-2.5 flex items-center space-x-1.5">
                <button
                  type="button"
                  (click)="openSettingsModal()"
                  id="menu-btn-open-wallet"
                  class="flex-1 py-1.5 px-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-[11px] flex items-center justify-center space-x-1 cursor-pointer shadow-xs transition-colors"
                >
                  <span>Gestionar Wallet</span>
                  <app-icon name="chevron-right" [size]="12" />
                </button>
                <button
                  type="button"
                  (click)="paySubscription()"
                  id="menu-btn-pay-quick-cr"
                  class="py-1.5 px-2 bg-white dark:bg-slate-900 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700/60 rounded-lg font-semibold text-[11px] cursor-pointer transition-colors"
                  title="Abonar cuota mensual (-100 CR)"
                >
                  -100 CR
                </button>
              </div>
            </div> -->

            <!-- Quick Action Links -->
            <div class="p-1 space-y-0.5">

              <button
                type="button"
                (click)="openSettingsModal()"
                id="menu-open-all-settings"
                class="w-full flex items-center justify-between px-3 py-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
              >
                <div class="flex items-center space-x-2.5">
                  <app-icon name="settings" [size]="16" class="text-slate-500 dark:text-slate-400" />
                  <span class="font-medium">{{ game.t('settings.title') }}</span>
                </div>
                <app-icon name="chevron-right" [size]="14" class="text-slate-400 dark:text-slate-500" />
              </button>

              <!-- Dark / Light Theme Toggle -->
              <button
                type="button"
                (click)="game.toggleDarkMode()"
                id="menu-toggle-theme"
                class="w-full flex items-center justify-between px-3 py-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
              >
                <div class="flex items-center space-x-2.5">
                  @if (game.isDarkMode()) {
                    <app-icon name="sun" [size]="16" class="text-amber-400" />
                    <span class="font-medium">{{ game.t('nav.toggle_theme_light') }}</span>
                  } @else {
                    <app-icon name="moon" [size]="16" class="text-slate-600 dark:text-slate-300" />
                    <span class="font-medium">{{ game.t('nav.toggle_theme_dark') }}</span>
                  }
                </div>
                <div class="w-8 h-4.5 rounded-full p-0.5 transition-colors" [ngClass]="game.isDarkMode() ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'">
                  <div class="w-3.5 h-3.5 rounded-full bg-white transition-transform" [ngClass]="game.isDarkMode() ? 'translate-x-3.5' : 'translate-x-0'"></div>
                </div>
              </button>

              <!-- Logout -->
              @if (game.currentUser()) {
                <div class="pt-1 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    (click)="logout()"
                    id="menu-logout"
                    class="w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer transition-colors font-semibold"
                  >
                    <app-icon name="log-out" [size]="15" />
                    <span>{{ game.t('nav.logout') }}</span>
                  </button>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <!-- Desktop Tabs Navigation (Material Tabs styled like section tabs) -->
  <nav class="hidden sm:block border-t border-slate-200/80 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/80">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex space-x-1.5 py-1.5">
        @for (item of navItems; track item.tab) {
          <a
            [routerLink]="['/' + item.tab]"
            (click)="game.setActiveTab(item.tab)"
            [id]="'nav-tab-' + item.tab"
            class="flex items-center space-x-2 px-3.5 py-2 text-xs md:text-sm rounded-xl transition-all cursor-pointer font-medium"
            [ngClass]="
              game.activeTab() === item.tab
                ? 'bg-white text-emerald-700 border border-slate-200/90 shadow-xs dark:bg-slate-800 dark:text-emerald-400 dark:border-slate-700 font-semibold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800/60'
            "
          >
            <span class="relative inline-flex">
              <app-icon [name]="item.iconName" [size]="16" [class]="game.activeTab() === item.tab ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'" />
              @if (item.tab === 'news' && newsRead.hasUnread()) {
                <span class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-900" aria-hidden="true"></span>
                <span class="sr-only">Noticias pendientes de leer</span>
              }
            </span>
            <span>{{ game.t(item.labelKey) }}</span>
          </a>
        }
      </div>
    </div>
  </nav>
</header>

<!-- Mobile Menu Drawer / Bottom Sheet (Native Mobile App Experience) -->
@if (isUserMenuOpen()) {
  <!-- Mobile Backdrop Overlay -->
  <div
    (click)="closeUserMenu()"
    class="sm:hidden fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs transition-opacity"
  ></div>

  <!-- Mobile Bottom Sheet Container -->
  <div
    id="mobile-user-menu"
    class="sm:hidden fixed inset-x-0 bottom-0 z-50 max-h-[88vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-3xl border-t border-slate-200 dark:border-slate-800 p-4 pb-8 shadow-2xl transition-transform animate-in slide-in-from-bottom duration-200"
  >
    <!-- Sheet Drag Indicator Handle -->
    <div class="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto mb-3.5"></div>

    <!-- Mobile Sheet Header -->
    <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800 mb-3">
      <div class="flex items-center space-x-2">
        <div class="w-8 h-8 rounded-xl bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
          <app-icon name="settings" [size]="18" />
        </div>
        <div>
          <h3 class="text-sm font-bold text-slate-900 dark:text-white">{{ game.t('settings.title') }}</h3>
          <p class="text-[11px] text-slate-500 dark:text-slate-400">{{ game.t('settings.subtitle') }}</p>
        </div>
      </div>
      <button
        type="button"
        (click)="closeUserMenu()"
        id="btn-close-mobile-menu"
        class="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
      >
        <app-icon name="x" [size]="18" />
      </button>
    </div>

    <!-- Manager Profile Card -->
    @if (game.currentUser(); as user) {
      <div class="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 mb-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-bold text-base shadow-sm shrink-0">
              {{ user.name.charAt(0) || 'M' }}
            </div>
            <div class="min-w-0">
              <p class="font-bold text-sm text-slate-900 dark:text-white truncate">{{ user.name }}</p>
              <p class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ user.email }}</p>
            </div>
          </div>
          <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            {{ user.role }}
          </span>
        </div>

        @if (game.userClub(); as club) {
          <div class="mt-3 pt-2.5 border-t border-slate-200/60 dark:border-slate-800 flex items-center justify-between text-xs">
            <span class="text-slate-500 dark:text-slate-400">{{ game.t('settings.profile_club') }}:</span>
            <span class="font-bold text-slate-900 dark:text-white flex items-center space-x-1.5">
              <span class="w-2.5 h-2.5 rounded-full" [style.backgroundColor]="club.primaryColor"></span>
              <span>{{ club.name }}</span>
            </span>
          </div>
        }

        @if (game.currentLeague(); as league) {
          <div class="mt-1.5 flex items-center justify-between text-xs">
            <span class="text-slate-500 dark:text-slate-400">{{ game.t('leagues.active_badge') }}:</span>
            <button
              type="button"
              (click)="closeUserMenu(); toggleContextSwitcher()"
              class="font-bold text-emerald-600 dark:text-emerald-400 flex items-center space-x-1 cursor-pointer"
            >
              <span>{{ league.name }}</span>
              <app-icon name="arrow-left-right" [size]="12" />
            </button>
          </div>
        }

        <div class="mt-1.5 flex items-center justify-between text-xs">
          <span class="text-slate-500 dark:text-slate-400">{{ game.t('nav.budget') }}:</span>
          <a
            [routerLink]="['/club']"
            [queryParams]="{ tab: 'economy' }"
            (click)="closeUserMenu()"
            class="font-mono font-bold text-emerald-600 dark:text-emerald-400 flex items-center space-x-1 cursor-pointer"
          >
            <app-icon name="coins" [size]="13" />
            <span>{{ game.balanceData() ? ((game.balanceData()!.availableBalance / 1000000).toFixed(2) + ' M€') : '—' }}</span>
          </a>
        </div>
      </div>
    }

    <!-- Manager Wallet (CR) Card 
    <div class="p-3.5 rounded-2xl bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-600/10 dark:from-amber-950/40 dark:via-orange-950/20 dark:to-amber-950/40 border border-amber-200/90 dark:border-amber-800/50 mb-3">
      <div class="flex items-center justify-between mb-1">
        <div class="flex items-center space-x-2">
          <app-icon name="coins" [size]="16" class="text-amber-600 dark:text-amber-400" />
          <span class="font-bold text-xs text-amber-900 dark:text-amber-200">
            {{ game.t('settings.wallet_title') }}
          </span>
        </div>
        <span class="font-mono font-black text-amber-700 dark:text-amber-300 text-sm px-2.5 py-0.5 rounded-lg bg-white/90 dark:bg-slate-950 border border-amber-200 dark:border-amber-800 shadow-2xs">
          {{ game.userClub().economy.subscriptionCredits }} CR
        </span>
      </div>
      <p class="text-[11px] text-amber-900/80 dark:text-amber-300/80 mt-1">
        {{ game.t('settings.wallet_monthly_fee') }}: 100 CR/mes.
      </p>

      <div class="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          (click)="openSettingsModal()"
          id="mobile-btn-open-wallet"
          class="py-2.5 px-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-xs flex items-center justify-center space-x-1.5 cursor-pointer shadow-xs"
        >
          <app-icon name="coins" [size]="14" />
          <span>{{ game.t('settings.wallet_open') }}</span>
        </button>

        <button
          type="button"
          (click)="paySubscription()"
          id="mobile-btn-pay-quick-cr"
          class="py-2.5 px-3 bg-white dark:bg-slate-950 hover:bg-amber-50 dark:hover:bg-slate-800 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700/60 rounded-xl font-bold text-xs cursor-pointer flex items-center justify-center space-x-1.5"
        >
          <app-icon name="check-circle" [size]="14" />
          <span>-100 CR</span>
        </button>
      </div>

      <div class="mt-2 flex items-center space-x-1.5 justify-center text-[10px] text-amber-800 dark:text-amber-400 font-semibold">
        <span>{{ game.t('settings.wallet_recharge') }}:</span>
        <button
          type="button"
          (click)="topUpCredits(250)"
          id="mobile-topup-250"
          class="px-2 py-0.5 bg-amber-200/80 dark:bg-amber-900/60 hover:bg-amber-300 rounded text-amber-900 dark:text-amber-200 cursor-pointer"
        >
          +250 CR
        </button>
        <button
          type="button"
          (click)="topUpCredits(500)"
          id="mobile-topup-500"
          class="px-2 py-0.5 bg-amber-200/80 dark:bg-amber-900/60 hover:bg-amber-300 rounded text-amber-900 dark:text-amber-200 cursor-pointer"
        >
          +500 CR
        </button>
      </div>
    </div>-->

    <!-- Quick Action Links -->
    <div class="space-y-1.5">
      <!-- Complete Settings Modal Link -->
      <button
        type="button"
        (click)="openSettingsModal()"
        id="mobile-open-all-settings"
        class="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-950 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-100 cursor-pointer transition-colors border border-slate-200/80 dark:border-slate-800"
      >
        <div class="flex items-center space-x-3">
          <div class="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
            <app-icon name="settings" [size]="16" />
          </div>
          <div class="text-left">
            <div class="text-xs font-bold">{{ game.t('settings.title') }}</div>
            <div class="text-[11px] text-slate-500 dark:text-slate-400">{{ game.t('settings.subtitle') }}</div>
          </div>
        </div>
        <app-icon name="chevron-right" [size]="16" class="text-slate-400 dark:text-slate-500" />
      </button>

      <!-- Theme Switcher -->
      <div class="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800">
        <div class="flex items-center space-x-3">
          <div class="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 flex items-center justify-center">
            @if (game.isDarkMode()) {
              <app-icon name="moon" [size]="16" class="text-indigo-400" />
            } @else {
              <app-icon name="sun" [size]="16" class="text-amber-500" />
            }
          </div>
          <div class="text-left">
            <div class="text-xs font-bold text-slate-900 dark:text-white">{{ game.t('nav.dark_mode_title') }}</div>
            <div class="text-[11px] text-slate-500 dark:text-slate-400">{{ game.isDarkMode() ? game.t('nav.dark_mode_enabled') : game.t('nav.light_mode_enabled') }}</div>
          </div>
        </div>

        <button
          type="button"
          (click)="game.toggleDarkMode()"
          id="mobile-toggle-theme"
          class="w-12 h-6.5 rounded-full p-0.5 transition-colors cursor-pointer"
          [ngClass]="game.isDarkMode() ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'"
        >
          <div
            class="w-5.5 h-5.5 rounded-full bg-white transition-transform shadow-xs flex items-center justify-center text-[10px]"
            [ngClass]="game.isDarkMode() ? 'translate-x-5.5' : 'translate-x-0'"
          >
            @if (game.isDarkMode()) {
              <span class="text-slate-800 text-[10px]">🌙</span>
            } @else {
              <span class="text-amber-600 text-[10px]">☀️</span>
            }
          </div>
        </button>
      </div>

      <!-- Logout Button -->
      @if (game.currentUser()) {
        <button
          type="button"
          (click)="logout()"
          id="mobile-btn-logout"
          class="w-full flex items-center justify-center space-x-2 p-3 rounded-xl text-rose-600 dark:text-rose-400 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-950/60 border border-rose-200 dark:border-rose-900/60 font-bold text-xs cursor-pointer transition-colors mt-2"
        >
          <app-icon name="log-out" [size]="15" />
          <span>{{ game.t('nav.logout') }}</span>
        </button>
      }
    </div>
  </div>
}

<!-- Mobile Bottom Navigation Bar (Material M3 Navigation Bar with the 5 core sections) -->
<nav
  class="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200/80 dark:border-slate-800 pt-1 pb-[calc(env(safe-area-inset-bottom,0px)+0.35rem)] px-2 flex justify-around items-center shadow-lg"
>
  @for (item of navItems; track item.tab) {
    <a
      [routerLink]="['/' + item.tab]"
      (click)="game.setActiveTab(item.tab)"
      [id]="'mobile-nav-' + item.tab"
      class="flex flex-col items-center py-1.5 px-2 rounded-xl text-[10px] font-medium transition-all cursor-pointer min-w-[56px]"
      [ngClass]="game.activeTab() === item.tab ? 'text-emerald-700 dark:text-emerald-400 font-bold' : 'text-slate-500 dark:text-slate-400'"
    >
      <span class="relative inline-flex">
              <app-icon [name]="item.iconName" [size]="20" [class]="game.activeTab() === item.tab ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'" />
              @if (item.tab === 'news' && newsRead.hasUnread()) {
                <span class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-900" aria-hidden="true"></span>
                <span class="sr-only">Noticias pendientes de leer</span>
              }
            </span>
      <span class="mt-0.5 leading-tight">{{ game.t(item.labelKey) }}</span>
    </a>
  }
</nav>

<!-- Modal de Ajustes & Wallet del Mánager -->
<app-settings-modal
  [isOpen]="isSettingsModalOpen()"
  (closed)="closeSettingsModal()"
/>

